/**
 * Trigger node for the automation flow builder.
 * Shows: trigger type, scope, schedule status.
 */
import { Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { TriggerNodeConfig } from '@/lib/dag-types';
import { getTriggerLabel } from '@/lib/automation-trigger-types';

interface TriggerNodeProps {
  config: TriggerNodeConfig;
  readOnly?: boolean;
  onClick?: () => void;
}

export function TriggerNode({ config, readOnly, onClick }: TriggerNodeProps) {
  const scopeLabel = config.scope_type === 'all'
    ? 'All Properties'
    : config.scope_type === 'selected'
    ? `${config.scope_property_ids?.length ?? 0} Properties`
    : 'Tagged';
  const scheduleLabel = config.schedule_type === 'scheduled' ? 'Scheduled' : 'Always Active';

  return (
    <div
      className="bg-wt-bg-secondary border border-wt-border-subtle rounded-lg overflow-hidden cursor-pointer hover:border-wt-green-primary/40 transition-colors"
      onClick={!readOnly ? onClick : undefined}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-wt-border-subtle">
        <span className="w-5 h-5 rounded-full bg-wt-bg-tertiary border border-wt-border-subtle flex items-center justify-center text-[10px] font-semibold font-mono text-wt-text-muted">
          1
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-wt-green-primary">
          WHEN
        </span>
      </div>
      <div className="p-3">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 rounded-md bg-wt-green-primary/10 border border-wt-green-primary/20 flex items-center justify-center shrink-0">
            <Zap className="h-4 w-4 text-wt-green-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-wt-text-primary">
              {config.title || getTriggerLabel(config.trigger_type)}
            </p>
            {config.subtitle && (
              <p className="text-xs text-wt-text-muted mt-0.5">{config.subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          <Badge variant="outline" className="text-[10px] h-5">
            {scopeLabel}
          </Badge>
          <Badge
            variant="outline"
            className={`text-[10px] h-5 ${
              config.schedule_type !== 'scheduled'
                ? 'bg-wt-green-primary/10 border-wt-green-primary/20 text-wt-green-primary'
                : ''
            }`}
          >
            {scheduleLabel}
          </Badge>
        </div>
      </div>
    </div>
  );
}
